import { useEffect, useRef, type RefObject } from "react";
import { BAR_LAYOUT_REM } from "../sections/HeaderV2/constants";
import { fitsMenuAtReference, resolveMenuFitReferenceWidth } from "../sections/HeaderV2/logic";

type UseMenuFitAtReferenceParams = {
  rootRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  signature: string;
  compact: boolean;
  rightMinRem: number | null;
  menuMarginRem: number;
  onChange?: (fits: boolean) => void;
};

const readRemPx = (): number => {
  if (typeof document === "undefined") return 16;
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
};

const measureMenuInOneLine = ({ list }: { list: HTMLElement }): number => {
  list.style.setProperty("--hv2-menu-wrap", "nowrap");
  list.style.setProperty("--hv2-menu-text-wrap", "nowrap");
  list.style.setProperty("width", "max-content");
  const width = list.offsetWidth;
  list.style.removeProperty("--hv2-menu-wrap");
  list.style.removeProperty("--hv2-menu-text-wrap");
  list.style.removeProperty("width");
  return width;
};

export const useMenuFitAtReference = ({
  rootRef,
  enabled,
  signature,
  compact,
  rightMinRem,
  menuMarginRem,
  onChange,
}: UseMenuFitAtReferenceParams) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const nav = rootRef.current?.querySelector<HTMLElement>("[data-header-menu]");
    const list = nav?.querySelector<HTMLElement>("ul");
    const bar = nav?.parentElement;

    const report = (fits: boolean) => {
      if (lastRef.current === fits) return;
      lastRef.current = fits;
      onChangeRef.current?.(fits);
    };

    if (!nav || !list || !bar || list.children.length === 0) {
      report(true);
      return;
    }

    const measure = () => {
      if (nav.offsetWidth === 0) return;
      const remPx = readRemPx();
      const fixedWidths = Array.from(bar.children)
        .filter((child) => child !== nav && !child.hasAttribute("data-header-right"))
        .map((child) => (child as HTMLElement).offsetWidth);
      report(
        fitsMenuAtReference({
          fixedWidths,
          menuWidth: measureMenuInOneLine({ list }),
          rightMinWidth: rightMinRem === null ? null : rightMinRem * remPx,
          gap: BAR_LAYOUT_REM.barGap * remPx,
          menuMargin: menuMarginRem * remPx,
          referenceWidth: resolveMenuFitReferenceWidth({ compact, remPx }),
        }),
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    Array.from(bar.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [rootRef, enabled, signature, compact, rightMinRem, menuMarginRem]);
};
